import { useToastContainer } from 'modules/layout/toastContainer';
import { PageWithHeader, StepCounter } from 'modules/ui';
import { ToastType } from 'modules/ui/toast';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { PAGE_SIZE } from 'modules/api/constants';
import { LeasingCalculatorSelectors, LeasingCalculatorThunks } from '../redux';
import { getLeasingCalculatorSteps } from './common/getTabs';
import { LeasingCalculatorData } from './common/LeasingCalculatorData';
import { getLeasingCalculatorPageUrl } from './common/helper';
import { StyledStepContainer } from './styled';
import { useAppDispatch, useAppSelector } from '../../hooks/appHooks';
import { LeasingInfoCalculation } from './infoCalculation';
import { LeasingCalculatorPathsEnum } from '../routes';

export interface LeasingCalculatorPageProps {
  step?: number;
  activeComponent?: React.ReactNode;
  showInfoCalculation?: boolean;
}

const initialCalculatorData: LeasingCalculatorData = {
  financialLeasingParticipation: 20,
  operationalLeasingParticipation: 0,
  infoInterestRate: 6.5,
  financialAdminCostPercentage: 1,
  operationalAdminCostPercentage: 1,
  leftoverPercentage: '100',
  leasingPaymentPeriodType: { id: 'Mjesečno', name: 'Mjesečno' },
};

export const LeasingCalculatorPage: React.FC<LeasingCalculatorPageProps> = ({
  step,
  activeComponent,
  showInfoCalculation,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const { addToast } = useToastContainer();

  const leasingOfferDetails = useAppSelector(
    LeasingCalculatorSelectors.selectProductSelectionDataResult,
  );

  const [leasingCalculatorFormData, setLeasingCalculatorFormData] =
    useState<LeasingCalculatorData>(initialCalculatorData);

  const handleSaveFormData = useCallback(
    (formData: LeasingCalculatorData) => {
      localStorage.setItem(
        'leasingCalculatorFormData',
        JSON.stringify({ ...leasingCalculatorFormData, ...formData }),
      );
      setLeasingCalculatorFormData((prevState) => ({
        ...prevState,
        ...formData,
      }));
    },
    [leasingCalculatorFormData],
  );

  const routeAfterDispatch = useCallback(
    (dispatchResult, path?: string) => {
      if (dispatchResult.meta.requestStatus === 'fulfilled') {
        if (path) {
          history.push(getLeasingCalculatorPageUrl(path));
        } else {
          history.push(getLeasingCalculatorPageUrl(step ? step + 1 : 1));
        }
      } else {
        addToast({
          type: ToastType.Urgent,
          title: t('leasingCalculator.stepErrorTitle'),
          text: t('leasingCalculator.stepErrorMessage'),
        });
      }
    },
    [addToast, history, step, t],
  );

  const handleNavigateToPreviousStep = useCallback(() => {
    if (step === 1) {
      history.push(getLeasingCalculatorPageUrl(0));
    } else {
      history.push(getLeasingCalculatorPageUrl(step ? step - 1 : 1));
    }
  }, [history, step]);

  const handleNavigateToNextStep = useCallback(async () => {
    switch (step) {
      case 1:
        history.push(getLeasingCalculatorPageUrl(2));
        break;
      case 2:
        history.push(getLeasingCalculatorPageUrl(3));
        break;
      case 3:
        routeAfterDispatch(
          await dispatch(
            LeasingCalculatorThunks.fetchPremadeProducts({
              params: {
                page: 1,
                size: PAGE_SIZE,
                search: '',
                sort: [],
              },
              calculatorData: {
                ...leasingCalculatorFormData,
                leasingObjectFirstRegistrationYear:
                  leasingCalculatorFormData.leasingObjectFirstRegistrationYear || {
                    id: 'Nije registrirano',
                    name: 'Nije registrirano',
                  },
              },
            }),
          ),
        );
        break;
    }
  }, [dispatch, history, leasingCalculatorFormData, routeAfterDispatch, step]);

  const handleFormSubmit = useCallback(async () => {
    if (leasingOfferDetails) {
      routeAfterDispatch(
        await dispatch(
          LeasingCalculatorThunks.requestFinancing(leasingOfferDetails?.number),
        ),
        LeasingCalculatorPathsEnum.success,
      );
    }
  }, [dispatch, leasingOfferDetails, routeAfterDispatch]);

  const stepForms = useMemo(
    () =>
      getLeasingCalculatorSteps(
        t,
        leasingCalculatorFormData,
        handleSaveFormData,
        handleNavigateToNextStep,
        handleNavigateToPreviousStep,
        handleFormSubmit,
      ),
    [
      t,
      leasingCalculatorFormData,
      handleSaveFormData,
      handleNavigateToNextStep,
      handleNavigateToPreviousStep,
      handleFormSubmit,
    ],
  );
  const [activeStep, setActiveStep] = useState<string>();

  const handleStepChange = useCallback(
    (key: string) => {
      if (key && key !== activeStep) {
        setActiveStep(key);
      }
    },
    [activeStep],
  );

  const ActiveStepForm = useMemo(
    () => stepForms.find((sf) => sf.key === activeStep),
    [activeStep, stepForms],
  );

  useEffect(() => {
    handleStepChange(`${step ? `${step}` : ''}`);
  }, [handleStepChange, step]);

  return (
    <PageWithHeader
      title={t('leasingCalculator.title')}
      description={t('leasingCalculator.description')}
      breadcrumbs={[
        { text: t('leasingCalculator.breadcrumbs.home'), url: '/' },
        { text: t('leasingCalculator.breadcrumbs.calculator'), url: '' },
      ]}
    >
      {/* Step Counter */}
      {step && (
        <StepCounter
          activeStep={step}
          stepDescriptions={stepForms.map((sf) => ({
            text: sf.name,
            url: getLeasingCalculatorPageUrl(sf.key),
          }))}
        />
      )}

      {/* Content */}
      {showInfoCalculation ? (
        <LeasingInfoCalculation
          defaultValues={leasingCalculatorFormData}
          onChange={handleSaveFormData}
        />
      ) : (
        <StyledStepContainer>
          {activeComponent || ActiveStepForm?.component}
        </StyledStepContainer>
      )}
    </PageWithHeader>
  );
};
